import React, { useMemo } from "react";
import Flatpickr from "react-flatpickr";
import { Turkish } from "flatpickr/dist/l10n/tr.js";
import "flatpickr/dist/flatpickr.css";

/**
 * Türkçe tarih+saat kutusu (gg/aa/yyyy ss:dd).
 *
 * {@link DateInput}'un saatli ikizi. Yerine geçtiği `<input type="datetime-local">`
 * hem biçimini hem takvim metinlerini tarayıcının dil ayarından alır: İngilizce
 * tarayıcıda alan aa/gg/yyyy --:-- AM görünür, açılan takvimde "Today", "Clear",
 * "September" yazar. Flatpickr ikisini de sabitler.
 *
 * Dışarıya karşı sözleşme `datetime-local` ile aynıdır: değer yyyy-MM-ddTHH:mm
 * alınır ve öyle verilir, API istekleri ve doğrulamalar değişmez.
 *
 * @param {string} value        yyyy-MM-ddTHH:mm ya da boş dize
 * @param {Function} onChange   Seçilen değeri alır; temizlenince "" gelir
 * @param {string} [minDate]    Alt sınır, aynı biçimde
 * @param {string} [maxDate]    Üst sınır, aynı biçimde
 * @param {boolean} [invalid]   reactstrap FormFeedback'in çalışması için is-invalid
 * @param {boolean} [disabled]
 * @param {string} [className]  form-control üzerine eklenir
 */
const pad = (n) => String(n).padStart(2, "0");

/** Date → "2026-09-20T20:24" (yerel saat; toISOString UTC'ye kaydırırdı). */
export const toLocalDateTime = (date) =>
    date
        ? `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
          `T${pad(date.getHours())}:${pad(date.getMinutes())}`
        : "";

/** "Şimdi"nin alan biçimindeki karşılığı — `min` sınırı için. */
export const nowLocalDateTime = () => toLocalDateTime(new Date());

// new Date("2026-09-20T20:24") motorlara göre değişebiliyor; parçalayıp yerel kuruyoruz.
const fromLocalDateTime = (value) => {
    if (!value) return "";
    const [datePart, timePart = "00:00"] = value.split("T");
    const [year, month, day] = datePart.split("-").map(Number);
    const [hour, minute] = timePart.split(":").map(Number);
    return year && month && day
        ? new Date(year, month - 1, day, hour || 0, minute || 0)
        : "";
};

const DateTimeInput = ({
    value,
    onChange,
    minDate,
    maxDate,
    invalid = false,
    disabled = false,
    className = "",
    placeholder = "gg/aa/yyyy ss:dd",
    ...rest
}) => {
    // react-flatpickr prop kimliğini karşılaştırır: her render'da yeni Date üretmek
    // setDate/set(minDate) çağırır ve allowInput ile yazarken metni sıfırlar.
    const dateValue = useMemo(() => fromLocalDateTime(value), [value]);
    const options = useMemo(() => ({
        locale: Turkish,
        dateFormat: "d/m/Y H:i",
        enableTime: true,
        time_24hr: true,
        allowInput: true,
        minDate: fromLocalDateTime(minDate) || undefined,
        maxDate: fromLocalDateTime(maxDate) || undefined,
    }), [minDate, maxDate]);

    return (
        <Flatpickr
            className={`form-control ${invalid ? "is-invalid" : ""} ${className}`.replace(/\s+/g, " ").trim()}
            value={dateValue}
            placeholder={placeholder}
            disabled={disabled}
            options={options}
            onChange={(dates) => onChange(toLocalDateTime(dates[0]))}
            {...rest}
        />
    );
};

export default DateTimeInput;
